/**
 * GenerationIndicator
 *
 * Global UI for background pick generation.
 * Shows:
 * 1. "Generating picks..." indicator with progress bar when generating in background (modal dismissed)
 * 2. "New picks ready!" notification when generation completes while user was browsing
 */

import { authAwareFetch } from '@contexts/authHttpUtils';
import { useGenerationStore } from '@stores/generationStore';
import { AlertTriangle } from 'lucide-react';
import { type ReactElement, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';

// Estimated total durations by analysis mode (in seconds)
const TOTAL_DURATIONS = {
  standard: 68, // ~68 seconds (Haiku - default, best hit rate)
  detailed: 95, // ~95 seconds (Sonnet)
  premium: 155, // ~155 seconds (Opus)
};

interface GenerationIndicatorProps {
  onLoadPicks?: () => void; // Navigate to picks tab when user clicks "Load Picks"
}

export function GenerationIndicator({
  onLoadPicks,
}: GenerationIndicatorProps): ReactElement | null {
  const {
    isGenerating,
    isDismissed,
    pendingPicks,
    analysisMode,
    cancelGeneration,
    clearPendingPicks,
    applyPendingPicks,
    reopenModal,
    getElapsedSeconds,
  } = useGenerationStore();

  const [elapsedSeconds, setElapsedSeconds] = useState(0);
  const [showCancelConfirm, setShowCancelConfirm] = useState(false);

  // Reset confirm state when generation ends
  useEffect(() => {
    if (!isGenerating) setShowCancelConfirm(false);
  }, [isGenerating]);

  // Update elapsed time every second when generating in background
  useEffect(() => {
    if (!isGenerating || !isDismissed) {
      setElapsedSeconds(0);
      return;
    }

    // Initial value
    setElapsedSeconds(getElapsedSeconds());

    const interval = setInterval(() => {
      setElapsedSeconds(getElapsedSeconds());
    }, 1000);

    return () => clearInterval(interval);
  }, [isGenerating, isDismissed, getElapsedSeconds]);

  // Show "Generating..." slim banner when generating in background
  if (isGenerating && isDismissed) {
    const totalDuration = TOTAL_DURATIONS[analysisMode];
    const progress = Math.min((elapsedSeconds / totalDuration) * 100, 99);

    return (
      <>
        <div
          onClick={reopenModal}
          className="-mx-3 sm:-mx-6 px-3 sm:px-6 pt-2 mt-2 border-t border-theme-border/30 cursor-pointer hover:bg-theme-surface-hover/50 transition-colors"
          title="Click to see full progress"
        >
          <div className="flex items-center gap-3">
            <div className="w-2 h-2 rounded-full bg-theme-accent animate-pulse shrink-0" />

            <div className="flex items-center gap-2 flex-1 min-w-0">
              <span className="text-xs font-medium text-theme-text whitespace-nowrap">
                Generating picks...
              </span>
              <span className="text-xs text-theme-accent font-medium">{Math.round(progress)}%</span>
            </div>

            <span className="text-[10px] text-theme-text-tertiary whitespace-nowrap hidden sm:inline">
              {Math.floor(elapsedSeconds / 60)}:{(elapsedSeconds % 60).toString().padStart(2, '0')}{' '}
              elapsed
            </span>

            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                setShowCancelConfirm(true);
              }}
              className="text-xs text-theme-danger hover:text-theme-danger-hover transition-colors px-2 py-1 shrink-0"
            >
              Cancel
            </button>
          </div>

          {/* Full-width thin progress bar */}
          <div className="h-1 bg-theme-surface-active rounded-full overflow-hidden mt-1.5 -mx-3 sm:-mx-6">
            <div
              className="h-full bg-gradient-to-r from-theme-primary to-theme-accent rounded-full transition-all duration-500"
              style={{ width: `${progress}%` }}
            />
          </div>
        </div>

        {/* Cancel confirmation modal — portaled to document.body.
            Must escape the AppHeader's `backdrop-blur-xl` container because
            backdrop-filter creates a new containing block in CSS, which
            breaks `position: fixed` — descendants would be positioned
            relative to the header instead of the viewport, pushing main
            content down instead of overlaying it. */}
        {showCancelConfirm &&
          createPortal(
            <div className="fixed inset-0 z-60 flex items-center justify-center p-4 overflow-y-auto">
              <div className="fixed inset-0 bg-black/70 backdrop-blur-sm" />
              <div className="relative bg-theme-surface border border-theme-border rounded-xl shadow-2xl max-w-md w-full p-6 my-auto animate-in fade-in zoom-in-95 duration-200">
                <div className="flex justify-center mb-4">
                  <div className="w-16 h-16 rounded-full flex items-center justify-center bg-theme-warning/50">
                    <AlertTriangle className="w-8 h-8 text-theme-warning" />
                  </div>
                </div>
                <h3 className="text-xl font-bold text-center mb-2 text-theme-warning">
                  Cancel Generation?
                </h3>
                <p className="text-theme-text text-center mb-6">
                  This still counts toward your daily limit. Any picks already generated will be
                  kept.
                </p>
                <div className="flex gap-3">
                  <button
                    type="button"
                    onClick={() => setShowCancelConfirm(false)}
                    className="flex-1 py-2.5 rounded-lg bg-theme-surface-hover hover:bg-theme-surface-active text-theme-text font-medium transition"
                  >
                    Keep Generating
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setShowCancelConfirm(false);
                      authAwareFetch('/api/picks/cancel', { method: 'POST' }).catch(() => {});
                      cancelGeneration();
                    }}
                    className="flex-1 py-2.5 rounded-lg bg-theme-danger/20 hover:bg-theme-danger/30 text-theme-danger font-medium transition"
                  >
                    Cancel Generation
                  </button>
                </div>
              </div>
            </div>,
            document.body,
          )}
      </>
    );
  }

  // Show "New picks ready!" banner when pending picks exist
  if (pendingPicks) {
    return (
      <div className="-mx-3 sm:-mx-6 px-3 sm:px-6 py-2 mt-2 border-t-2 border-theme-success/50 bg-theme-success/5">
        <div className="flex items-center justify-between gap-3">
          <div className="flex items-center gap-2 min-w-0">
            <span className="text-sm">✨</span>
            <span className="text-xs font-medium text-theme-text">New picks ready!</span>
            <span className="text-[10px] text-theme-text-secondary">
              {pendingPicks.picks.length} parlays generated
            </span>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <button
              type="button"
              onClick={() => {
                if (onLoadPicks) onLoadPicks();
                applyPendingPicks();
              }}
              className="px-3 py-1 text-xs bg-theme-success hover:bg-theme-success-hover text-theme-text-inverse rounded-md font-medium transition-colors"
            >
              Load Picks
            </button>
            <button
              type="button"
              onClick={clearPendingPicks}
              className="px-3 py-1 text-xs bg-theme-surface-hover hover:bg-theme-surface-active text-theme-text-secondary rounded-md transition-colors"
            >
              Dismiss
            </button>
          </div>
        </div>
      </div>
    );
  }

  return null;
}
